import { Facebook, Instagram, Youtube, Phone, Mail, Clock, MapPin, Heart, FileText, Globe } from "lucide-react";
import { Link, useNavigate } from "react-router-dom";

interface FooterProps {
  onOpenModal: (type: "surat" | "aspirasi" | "login" | "umkm") => void;
}

export default function Footer({ onOpenModal }: FooterProps) {
  
  const navigate = useNavigate();

  const handleMenuClick = (path: string) => {
    navigate(path);
  };

  const handleLayananClick = (srv: string) => {
    if (srv === "Surat Pengantar" || srv === "Surat Domisili") {
      onOpenModal("surat");
    } else if (srv === "Pendaftaran UMKM" || srv === "Surat Usaha") {
      navigate("/umkm/daftar");
    } else if (srv === "Aspirasi Warga" || srv === "Pengaduan") {
      onOpenModal("aspirasi");
    } else {
      // scroll to agenda
      navigate("/informasi");
    }
  };

  return (
    <footer id="kontak" className="bg-[#002f1a] text-gray-100 pt-16 pb-8 border-t border-green-800">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        
        {/* Main Footer Directory layout - 5 Columns according to mockup */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-12 gap-8 pb-12 border-b border-green-950">
          
          {/* Column 1: Brand details (3/12 span) */}
          <div className="lg:col-span-3 space-y-5">
            <div className="flex items-center gap-3">
              <div className="relative flex items-center justify-center w-11 h-11 bg-white rounded-full border border-[#ffd22e] p-0.5 overflow-hidden shadow">
                <img 
                  src="/images/logo.jpeg" 
                  alt="Logo Cibubur Villa 2" 
                  className="w-full h-full object-cover rounded-full"
                  referrerPolicy="no-referrer"
                />
              </div>
              <div className="flex flex-col">
                <p className="text-white text-base font-extrabold tracking-widest leading-none">CIBUBUR</p>
                <p className="text-secondary text-xs font-bold tracking-tight">VILLA 2 DIGITAL</p>
              </div>
            </div>

            <p className="text-xs text-green-100/75 leading-relaxed">
              Portal Resmi RW 14 Perumahan Cibubur Villa 2 - Harjamukti, Cimanggis - Depok. Wadah kolaborasi digital, kehumasan, pelayanan administrasi terpadu &amp; promosi produk UMKM lokal kompleks.
            </p>

            {/* Social icons with custom layouts */}
            <div className="flex gap-2.5">
              {[
                { icon: <Facebook className="w-4 h-4" />, name: "Facebook Messenger", link: "https://facebook.com" },
                { 
                  icon: (
                    <svg viewBox="0 0 24 24" className="w-4 h-4 fill-current">
                      <path d="M12.012 2c-5.506 0-9.988 4.482-9.988 9.988 0 1.761.459 3.475 1.332 5.006L2 22l5.127-1.344c1.472.803 3.125 1.258 4.885 1.258 5.506 0 10.001-4.482 10.001-9.988S17.518 2 12.012 2zm6.275 14.156c-.258.729-1.293 1.329-1.815 1.405-.461.066-1.062.109-1.696-.092-.405-.128-.921-.303-1.579-.584-2.827-1.205-4.66-4.083-4.801-4.272-.14-.19-1.127-1.498-1.127-2.858 0-1.36.702-2.028.95-2.302.248-.274.544-.343.727-.343h.52c.165 0 .386.012.56.42.181.42.619 1.503.673 1.614.053.111.089.24.015.389-.074.148-.112.24-.223.369-.112.128-.236.287-.336.386-.112.111-.23.232-.099.458.13.226.58 1.157 1.244 1.748.857.76 1.579.996 1.802 1.109.224.113.354.095.485-.054.13-.149.56-.653.71-.876.149-.223.298-.186.502-.11.205.076 1.302.614 1.526.726.223.111.372.167.428.261.056.095.056.549-.202 1.278z" />
                    </svg>
                  ), 
                  name: "WhatsApp Helpline", 
                  link: "https://wa.me/6281200000000" 
                },
                { icon: <Instagram className="w-4 h-4" />, name: "Instagram Profile", link: "https://instagram.com" },
                { icon: <Youtube className="w-4 h-4" />, name: "YouTube Channel", link: "https://youtube.com" }
              ].map((soc, idx) => (
                <a 
                  key={idx}
                  href={soc.link} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="w-8 h-8 rounded-full bg-white/5 border border-white/10 flex items-center justify-center hover:bg-secondary hover:text-primary transition-all duration-300"
                  aria-label={soc.name}
                >
                  {soc.icon}
                </a>
              ))}
            </div>
          </div>

          {/* Column 2: Navigation link directory (2/12 span) */}
          <div className="lg:col-span-2 space-y-4">
            <h4 className="text-sm font-extrabold text-secondary tracking-widest uppercase font-sans">
              MENU
            </h4>
            <ul className="space-y-2.5 text-xs text-green-100/80 font-medium">
              {[
                { name: "Beranda", path: "/" },
                { name: "Profil RW", path: "/profil" },
                { name: "Layanan", path: "/layanan" },
                { name: "Informasi", path: "/informasi" },
                { name: "UMKM", path: "/umkm" },
                { name: "Galeri", path: "/galeri" },
                { name: "Kontak", path: "/kontak" }
              ].map((lnk) => (
                <li key={lnk.name}>
                  <button 
                    onClick={() => handleMenuClick(lnk.path)} 
                    className="hover:text-secondary hover:translate-x-1 transition-all text-left cursor-pointer"
                  >
                    &rsaquo; {lnk.name}
                  </button>
                </li>
              ))}
            </ul>
          </div>

          {/* Column 3: Layanan quick items (2/12 span) */}
          <div className="lg:col-span-2 space-y-4">
            <h4 className="text-sm font-extrabold text-secondary tracking-widest uppercase font-sans">
              LAYANAN
            </h4>
            <ul className="space-y-2.5 text-xs text-green-100/80 font-medium">
              {[
                "Surat Pengantar",
                "Surat Domisili",
                "Pendaftaran UMKM",
                "Aspirasi Warga",
                "Pengaduan",
                "Agenda Kegiatan"
              ].map((srv) => (
                <li key={srv}>
                  <button 
                    onClick={() => handleLayananClick(srv)}
                    className="hover:text-secondary hover:translate-x-1 transition-all text-left cursor-pointer"
                  >
                    &rsaquo; {srv}
                  </button>
                </li>
              ))}
            </ul>
          </div>

          {/* Column 4: Contact RW (2/12 span) */}
          <div className="lg:col-span-2 space-y-4 font-sans">
            <h4 className="text-sm font-extrabold text-secondary tracking-widest uppercase">
              KONTAK RW
            </h4>
            
            <div className="space-y-3 text-[11px] sm:text-xs text-green-100/80 font-medium">
              <p className="leading-relaxed flex items-start gap-1.5">
                <MapPin className="w-4 h-4 text-accent shrink-0 mt-0.5" />
                <span>Sekretariat RW 14 Cibubur Villa 2, Harjamukti, Cimanggis, Depok 16455</span>
              </p>

              <div className="flex flex-col gap-1">
                <a href="tel:081222223720" className="flex items-center gap-1.5 hover:text-secondary">
                  <Phone className="w-3.5 h-3.5 text-accent shrink-0" />
                  <span>081-22222-3720</span>
                </a>
                <a href="mailto:info@cibuburvilla2.id" className="flex items-center gap-1.5 hover:text-secondary pt-0.5">
                  <Mail className="w-3.5 h-3.5 text-accent shrink-0" />
                  <span>info@cibuburvilla2.id</span>
                </a>
              </div>

              <p className="flex items-center gap-1.5 pt-1 border-t border-green-950 text-[10px] text-[#ffd22e]">
                <Clock className="w-3.5 h-3.5" />
                <span>Senin - Sabtu (08.00 - 17.00)</span>
              </p>
            </div>
          </div>

          {/* Column 5: Lokasi Map Mockup container matching the mockup beautifully (3/12 span) */}
          <div className="lg:col-span-3 space-y-4">
            <h4 className="text-sm font-extrabold text-secondary tracking-widest uppercase font-sans">
              LOKASI
            </h4>
            <div className="relative h-28 w-full rounded-2xl overflow-hidden border border-green-800/40 bg-green-950 shadow-inner group">
              <iframe 
                src="https://maps.google.com/maps?q=Cibubur%20Villa%202,%20Harjamukti,%20Cimanggis,%20Depok&t=&z=14&ie=UTF8&iwloc=&output=embed" 
                className="w-full h-full border-0 opacity-80 group-hover:opacity-100 transition-opacity duration-300" 
                allowFullScreen={true}
                loading="lazy" 
                referrerPolicy="no-referrer-when-downgrade"
                title="Peta Lokasi Cibubur Villa 2"
              />
            </div>
          </div>

        </div>

        {/* Bottom Footer segment */}
        <div className="pt-8 flex flex-col md:flex-row justify-between items-center text-[11px] text-green-100/60 font-medium gap-4">
          <p className="text-center md:text-left">
            &copy; {new Date().getFullYear()} Cibubur Villa 2 Digital. All Rights Reserved.
          </p>
          <p className="flex items-center gap-1 text-center font-sans">
            <span>Dibuat dengan hati</span>
            <Heart className="w-3.5 h-3.5 text-red-500 fill-current ml-0.5" />
            <span>untuk Warga Cibubur Villa 2</span>
          </p>
        </div>

      </div>
    </footer>
  );
}
